import { appApi } from "@/api/modules/app";

const WECHAT_JS_SDK_URL = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
const WECHAT_VOICE_API_LIST = ["startRecord", "stopRecord", "translateVoice"];
const WECHAT_JS_API_LIST = ["checkJsApi", ...WECHAT_VOICE_API_LIST];
const WECHAT_MIN_RECORD_DURATION_MS = 1200;

let sdkLoading: Promise<void> | null = null;
let configuredUrl = "";
let configReady: Promise<void> | null = null;
let supportedApisReady: Promise<void> | null = null;
let recordStartedAt = 0;

export const isWechatBrowser = () =>
  typeof navigator !== "undefined" && /MicroMessenger/i.test(navigator.userAgent);

const isWechatIos = () =>
  typeof navigator !== "undefined" && /MicroMessenger/i.test(navigator.userAgent) && /iP(hone|ad|od)/i.test(navigator.userAgent);

const normalizeSignUrl = (href: string) => {
  const url = new URL(href);
  url.hash = "";
  return url.toString();
};

const wechatEntrySignUrl = typeof window === "undefined" ? "" : normalizeSignUrl(window.location.href);

const currentSignUrl = () => (isWechatIos() ? wechatEntrySignUrl : normalizeSignUrl(window.location.href));

const describeWechatError = (fallback: string, error?: unknown) => {
  if (error && typeof error === "object" && "errMsg" in error) {
    return `${fallback}: ${String((error as { errMsg?: unknown }).errMsg || "")}`;
  }
  return fallback;
};

const resolveWechatLocalId = (result: { localId?: string; local_id?: string }) =>
  String(result.localId || result.local_id || "").trim();

const isWechatRecordTooShortError = (error: unknown) => {
  const errMsg = error && typeof error === "object" && "errMsg" in error
    ? String((error as { errMsg?: unknown }).errMsg || "")
    : String(error || "");
  return /tooshort|too short/i.test(errMsg);
};

const waitForMinimumRecordDuration = async () => {
  if (!recordStartedAt) {
    return;
  }
  const remaining = WECHAT_MIN_RECORD_DURATION_MS - (Date.now() - recordStartedAt);
  if (remaining > 0) {
    await new Promise((resolve) => window.setTimeout(resolve, remaining));
  }
};

const loadWechatSdk = () => {
  if (window.wx) {
    return Promise.resolve();
  }
  if (sdkLoading) {
    return sdkLoading;
  }
  sdkLoading = new Promise<void>((resolve, reject) => {
    const existed = document.querySelector<HTMLScriptElement>(`script[src="${WECHAT_JS_SDK_URL}"]`);
    if (existed) {
      if (window.wx) {
        resolve();
        return;
      }
      existed.addEventListener("load", () => resolve(), { once: true });
      existed.addEventListener("error", () => reject(new Error("微信录音组件加载失败")), { once: true });
      window.setTimeout(() => {
        if (window.wx) {
          resolve();
        }
      }, 0);
      return;
    }
    const script = document.createElement("script");
    script.src = WECHAT_JS_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("微信录音组件加载失败"));
    document.head.appendChild(script);
  });
  return sdkLoading;
};

const ensureWechatVoiceReady = async () => {
  if (!isWechatBrowser()) {
    return false;
  }
  await loadWechatSdk();
  if (!window.wx) {
    throw new Error("微信录音组件不可用");
  }
  const signUrl = currentSignUrl();
  if (configReady && configuredUrl === signUrl) {
    await configReady;
    await ensureWechatVoiceApisSupported();
    return true;
  }
  configuredUrl = signUrl;
  supportedApisReady = null;
  configReady = appApi.getWechatJsConfig({ url: signUrl }).then((config) => new Promise<void>((resolve, reject) => {
    if (!config?.appId || !config.signature) {
      reject(new Error("微信录音签名未配置"));
      return;
    }
    window.wx?.ready(() => resolve());
    window.wx?.error((error) => reject(error instanceof Error ? error : new Error(describeWechatError("微信录音初始化失败", error))));
    window.wx?.config({
      debug: false,
      appId: config.appId,
      timestamp: config.timestamp,
      nonceStr: config.nonceStr,
      signature: config.signature,
      jsApiList: WECHAT_JS_API_LIST,
    });
  }));
  try {
    await configReady;
    await ensureWechatVoiceApisSupported();
  } catch (error) {
    configReady = null;
    supportedApisReady = null;
    throw error;
  }
  return true;
};

const ensureWechatVoiceApisSupported = async () => {
  if (supportedApisReady) {
    return supportedApisReady;
  }
  supportedApisReady = new Promise<void>((resolve, reject) => {
    window.wx?.checkJsApi({
      jsApiList: WECHAT_VOICE_API_LIST,
      success: (result) => {
        const checkResult = result.checkResult || {};
        const unsupported = WECHAT_VOICE_API_LIST.filter((api) => checkResult[api] === false);
        if (unsupported.length > 0) {
          reject(new Error(`微信环境不支持录音接口: ${unsupported.join(",")}`));
          return;
        }
        resolve();
      },
      fail: (error) => reject(new Error(describeWechatError("微信录音接口检查失败", error))),
    });
  });
  return supportedApisReady;
};

export const preloadWechatVoice = async () => {
  await ensureWechatVoiceReady();
};

export const startWechatVoiceRecord = async () => {
  const ready = await ensureWechatVoiceReady();
  if (!ready) {
    return false;
  }
  await new Promise<void>((resolve, reject) => {
    window.wx?.startRecord({
      success: () => {
        recordStartedAt = Date.now();
        resolve();
      },
      cancel: () => reject(new Error("已取消微信麦克风授权")),
      fail: (error) => reject(new Error(describeWechatError("微信录音启动失败", error))),
    });
  });
  return true;
};

export const stopWechatVoiceAndTranslate = async () => {
  const ready = await ensureWechatVoiceReady();
  if (!ready) {
    return "";
  }
  await waitForMinimumRecordDuration();
  const localId = await new Promise<string>((resolve, reject) => {
    window.wx?.stopRecord({
      success: (result) => {
        recordStartedAt = 0;
        const voiceLocalId = resolveWechatLocalId(result);
        if (!voiceLocalId) {
          reject(new Error("微信录音没有生成可转换的语音内容，请按住说完整后再松手"));
          return;
        }
        resolve(voiceLocalId);
      },
      fail: (error) => {
        recordStartedAt = 0;
        if (isWechatRecordTooShortError(error)) {
          reject(new Error("录音时间太短，请按住说完整后再松手"));
          return;
        }
        reject(new Error(describeWechatError("微信录音结束失败", error)));
      },
    });
  });
  const transcript = await new Promise<string>((resolve, reject) => {
    window.wx?.translateVoice({
      localId,
      isShowProgressTips: 1,
      success: (result) => resolve(String(result.translateResult || "").trim()),
      fail: (error) => reject(new Error(describeWechatError("微信语音转文字失败", error))),
    });
  });
  return transcript;
};
